import { KeyboardEvent, useRef, useState } from 'react';

export type RovingTabIndexOrientation =
  | 'horizontal'
  | 'vertical'
  | 'both'
  | 'grid';

type UseRovingTabIndexOptions = {
  itemCount: number;
  /**
   * Index of the item that should carry the group's Tab stop — normally the
   * selected one, so tabbing in lands on it. Defaults to `0`; pass `0` when
   * nothing is selected. Changing it moves the Tab stop to match without
   * moving focus, keeping the entry point aligned with a selection that
   * changed from outside the group.
   */
  selectedIndex?: number;
  /**
   * Which arrow keys move focus:
   * - `'horizontal'` — Left/Right only.
   * - `'vertical'` — Up/Down only.
   * - `'both'` (default) — all four arrows move through a single linear sequence.
   * - `'grid'` — Left/Right move linearly; Up/Down move to the item in the
   *   adjacent row nearest the current column, resolved from rendered geometry
   *   (handles wrapping grids with a variable column count / ragged last row).
   */
  orientation: RovingTabIndexOrientation;
  /** Whether Left/Right (and linear) navigation wraps around the ends. Defaults to `true`. */
  loop?: boolean;
};

export type RovingTabIndexItemProps<T extends HTMLElement> = {
  ref: (node: T | null) => void;
  tabIndex: 0 | -1;
  onKeyDown: (event: KeyboardEvent<T>) => void;
};

type UseRovingTabIndexResult<T extends HTMLElement> = {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  getRovingTabIndexItemProps: (index: number) => RovingTabIndexItemProps<T>;
};

const wrapIndex = (index: number, length: number): number =>
  (index + length) % length;

/**
 * Finds the item in the row directly above (`direction === -1`) or below
 * (`direction === 1`) the item at `from`, picking the one whose column (left
 * offset) is closest to it. Returns `null` when there is no adjacent row.
 */
const findAdjacentRowIndex = <T extends HTMLElement>(
  items: (T | null)[],
  from: number,
  direction: -1 | 1
): number | null => {
  const current = items[from];
  if (!current) return null;
  const { offsetTop: currentTop, offsetLeft: currentLeft } = current;

  const adjacentRowTops = items
    .filter((item): item is T => item !== null)
    .map(item => item.offsetTop)
    .filter(top => (direction < 0 ? top < currentTop : top > currentTop));
  if (adjacentRowTops.length === 0) return null;

  const targetTop =
    direction < 0 ? Math.max(...adjacentRowTops) : Math.min(...adjacentRowTops);

  const rowCandidates = items
    .map((item, index) => ({ item, index }))
    .filter(
      (entry): entry is { item: T; index: number } =>
        entry.item !== null && entry.item.offsetTop === targetTop
    );

  return rowCandidates.reduce((best, candidate) =>
    Math.abs(candidate.item.offsetLeft - currentLeft) <
    Math.abs(best.item.offsetLeft - currentLeft)
      ? candidate
      : best
  ).index;
};

/**
 * Implements the roving tabindex pattern for a group of focusable items: the
 * group is a single Tab stop (only `activeIndex` is tabbable, the rest are
 * `tabIndex={-1}`) and arrow keys / Home / End move focus between items.
 *
 * The hook only manages focus; the consuming component owns the accessible
 * structure. Give the container a role that implies a composite widget with
 * managed children and give the items the child role that role requires:
 * `listbox`/`option`, `menu`/`menuitem`, `radiogroup`/`radio`,
 * `tablist`/`tab`, or `toolbar` with plain buttons. Note that a child role
 * replaces the element's native role, so the item must not also rely on being
 * announced as a button — `toolbar` is the one that keeps real buttons, and it
 * is the right choice for a set of buttons carrying `aria-pressed`.
 *
 * `grid` is rarely the right container here: `gridcell` must be owned by a
 * `row`, and a `gridcell` is a container for interactive content rather than
 * something interactive itself. Reach for it only when real rows exist in the
 * DOM — a wrapping flex layout has visual rows, not structural ones.
 *
 * `orientation: 'grid'` above concerns key handling only and carries no ARIA
 * implication; two-dimensional arrow movement is fine in a `toolbar` or
 * `listbox`.
 *
 * Spread `getRovingTabIndexItemProps(index)` onto each item to wire up its
 * `ref`, `tabIndex` and `onKeyDown`.
 *
 * `activeIndex` tracks where the Tab stop sits, which arrow keys move and
 * `selectedIndex` re-seeds; it is not the selected value. Owning selection
 * stays with the consumer, which should keep `selectedIndex` pointed at it.
 */
export const useRovingTabIndex = <T extends HTMLElement>({
  itemCount,
  selectedIndex = 0,
  orientation,
  loop = true,
}: UseRovingTabIndexOptions): UseRovingTabIndexResult<T> => {
  const itemRefs = useRef<(T | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex);
  const [appliedSelectedIndex, setAppliedSelectedIndex] =
    useState<number>(selectedIndex);

  if (selectedIndex !== appliedSelectedIndex) {
    setAppliedSelectedIndex(selectedIndex);
    setActiveIndex(selectedIndex);
  }

  const focusItem = (index: number) => {
    setActiveIndex(index);
    itemRefs.current[index]?.focus();
  };

  const move = (delta: number) => {
    const next = activeIndex + delta;
    focusItem(
      loop
        ? wrapIndex(next, itemCount)
        : Math.min(Math.max(next, 0), itemCount - 1)
    );
  };

  const moveVertically = (direction: -1 | 1) => {
    if (orientation !== 'grid') {
      move(direction);
      return;
    }
    const target = findAdjacentRowIndex(
      itemRefs.current,
      activeIndex,
      direction
    );
    if (target !== null) focusItem(target);
  };

  const onKeyDown = (event: KeyboardEvent<T>) => {
    const handlesHorizontal = orientation !== 'vertical';
    const handlesVertical = orientation !== 'horizontal';
    switch (event.key) {
      case 'ArrowRight':
        if (!handlesHorizontal) return;
        event.preventDefault();
        move(1);
        break;
      case 'ArrowDown':
        if (!handlesVertical) return;
        event.preventDefault();
        moveVertically(1);
        break;
      case 'ArrowLeft':
        if (!handlesHorizontal) return;
        event.preventDefault();
        move(-1);
        break;
      case 'ArrowUp':
        if (!handlesVertical) return;
        event.preventDefault();
        moveVertically(-1);
        break;
      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        focusItem(itemCount - 1);
        break;
    }
  };

  const getRovingTabIndexItemProps = (
    index: number
  ): RovingTabIndexItemProps<T> => ({
    ref: (node: T | null) => {
      itemRefs.current[index] = node;
    },
    tabIndex: index === activeIndex ? 0 : -1,
    onKeyDown,
  });

  return { activeIndex, setActiveIndex, getRovingTabIndexItemProps };
};
